// app/components/ForgotPasswordModal.tsx
"use client";

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  Alert,
  styled,
  alpha,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CustomTextField from "@/app/atoms/TextField";
import apiClient from "@/app/utils/apiClient";
import Button from "../atoms/Button";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    padding: theme.spacing(2),
    maxWidth: 420,
    // background: `linear-gradient(135deg, ${theme.palette.background.default}, ${alpha(theme.palette.primary.main, 0.02)})`,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  margin: "0 auto",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
  marginBottom: theme.spacing(2),
}));

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  open,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySent, setAlreadySent] = useState(false);

  // ✅ Simple local cooldown for Forgot Password (no userId before submit)
  const [cooldownTime, setCooldownTime] = useState(0);
  const [isInCooldown, setIsInCooldown] = useState(false);

  // Countdown effect
  useEffect(() => {
    if (isInCooldown && cooldownTime > 0) {
      const interval = setInterval(() => {
        setCooldownTime((prev) => {
          if (prev <= 1) {
            setIsInCooldown(false);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isInCooldown, cooldownTime]);

  const formattedTime = `${Math.floor(cooldownTime / 60)
    .toString()
    .padStart(2, "0")}:${(cooldownTime % 60).toString().padStart(2, "0")}`;

  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setError(null);
  //   setLoading(true);

  //   try {
  //     await apiClient.post("/auth/forgot-password", { email });
  //     setSuccess(true);
  //     setSubmitted(true);
  //     // ✅ Start 5-minute cooldown
  //     setIsInCooldown(true);
  //     setCooldownTime(5 * 60);
  //   } catch (err: any) {
  //     setError(err.response?.data?.message || "Failed to send reset link");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Customer site: app/components/ForgotPasswordModal.tsx
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // ✅ Send panel information
      const res = await apiClient.post("/auth/forgot-password", {
        email,
        panel: "customer", // 👈 Specify this is from customer panel
      });
      // Backend skips sending if a valid (unexpired) link already exists
      setAlreadySent(Boolean(res.data?.alreadySent));
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError(null);
    setSuccess(false);
    setSubmitted(false);
    setAlreadySent(false);
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      {success ? (
        <>
          <DialogContent>
            <Box textAlign="center" py={3}>
              <IconWrapper>
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 36, color: "success.main" }}
                />
              </IconWrapper>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                {alreadySent ? "Reset Link Already Sent!" : "Reset Link Sent!"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {alreadySent ? (
                  <>
                    We already sent a reset link to <strong>{email}</strong>{" "}
                    recently. Please check your inbox and use the link from
                    that email — it is still valid.
                  </>
                ) : (
                  <>
                    If an account exists for <strong>{email}</strong>, you will
                    receive a password reset link shortly.
                  </>
                )}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, display: "block" }}
              >
                Please check your inbox and spam folder.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="contained" fullWidth curved>
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <Typography variant="h6" textAlign="center" fontWeight={600}>
              Forgot Password
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box textAlign="center" mb={3}>
              <IconWrapper>
                <EmailOutlinedIcon
                  sx={{ fontSize: 36, color: "primary.main" }}
                />
              </IconWrapper>
              <Typography variant="body2" color="text.secondary">
                Enter your email address and we'll send you a link to reset your
                password.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <CustomTextField
              name="email"
              type="email"
              placeholder="Enter your email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              startIcon={<EmailOutlinedIcon />}
            />

            {isInCooldown && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ mt: 1, display: "block", textAlign: "center" }}
              >
                ⏳ Next request available in {formattedTime}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              curved
              disabled={loading || !email || isInCooldown}
              sx={{ py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : isInCooldown ? (
                `Wait ${formattedTime}`
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </DialogActions>
        </form>
      )}
    </StyledDialog>
  );
};

export default ForgotPasswordModal;
